/**
 * Drives an element's `transform` from a damped follow target via
 * requestAnimationFrame, bypassing React reconciliation — the compact-mode
 * counterpart of the workspace cursor's rAF engine (use-cursor-animation.ts).
 *
 * Targets are GLOBAL (CG-screen) coordinates; each per-display window
 * subtracts its own window origin at write time, so whichever display-owned
 * mirror renders the bubble stays aligned to the shared global target.
 *
 * Damping is delta-time corrected (dampPoint), so the trail distance is
 * identical across 60Hz/120Hz displays, and a long rAF gap (deprioritized
 * mirror window) resolves to a snap rather than a visible catch-up glide.
 */
import { useLayoutEffect, useMemo, useRef } from "react";
import {
  dampPoint,
  easeInOutCubic,
  penTravel,
  penTravelPoint,
  type PenTravelPath,
} from "@/components/cursors/cursor-animation-geometry";
import { getWindowOrigin } from "@/desktop/use-window-origin";

const SETTLED_DISTANCE_PX = 0.1;
const noop = () => {};
const keepPosition = (position: { x: number; y: number }) => position;

function writePosition(
  el: HTMLDivElement,
  pos: { x: number; y: number },
): void {
  const origin = getWindowOrigin();
  const transform = `translate3d(${pos.x - origin.x}px, ${pos.y - origin.y}px, 0)`;
  if (el.style.transform !== transform) el.style.transform = transform;
}

export interface DampedFollowTarget {
  /** Global (CG-screen) coordinates, offsets already applied. */
  x: number;
  y: number;
  /** Optional global position to animate from when the element has not
   *  rendered a prior target. Presentation cursors use the live pointer. */
  initialPosition?: { x: number; y: number };
  /** Snap instantly instead of gliding (fresh point-target mounts). */
  snap?: boolean;
  settleKey?: string;
  onSettled?: (key: string) => void;
  durationMs?: number;
  /** Adjusts every damped step (not snaps or timed travel) against the
   *  target, e.g. to keep a lagging element off the pointer tip. */
  constrain?: (
    position: { x: number; y: number },
    target: { x: number; y: number },
  ) => { x: number; y: number };
}

export interface DampedFollowHandle {
  /** Callback ref for the positioned element. The element must use
   *  `position: fixed; left: 0; top: 0` — the hook drives `transform`. */
  elementRef: (el: HTMLDivElement | null) => void;
  /** Record every render. The hook applies the latest committed target after
   *  commit. `null` hides/pauses and resets so the next target snaps. */
  setTarget: (target: DampedFollowTarget | null) => void;
  /** Current rendered global position, used to freeze interactive content
   *  without jumping it back to the user's faster-moving pointer. */
  getPosition: () => Readonly<{ x: number; y: number }> | null;
}

export function useDampedFollow(smoothingSeconds: number): DampedFollowHandle {
  const elRef = useRef<HTMLDivElement | null>(null);
  const pendingTargetRef = useRef<DampedFollowTarget | null>(null);
  const committedTargetRef = useRef<DampedFollowTarget | null>(null);
  const posRef = useRef<{ x: number; y: number } | null>(null);
  const lastTimeRef = useRef(0);
  const smoothingRef = useRef(smoothingSeconds);
  const startLoopRef = useRef<() => void>(noop);
  const stopLoopRef = useRef<() => void>(noop);
  const settledKeyRef = useRef<string | null>(null);
  /** Timed travel follows the bowed path captured when it starts. */
  const travelRef = useRef<PenTravelPath | null>(null);
  const travelStartTimeRef = useRef(0);

  const startTravel = (
    start: { x: number; y: number } | null,
    target: DampedFollowTarget,
  ) => {
    travelRef.current = start
      ? penTravel({ ...start }, { x: target.x, y: target.y }, target.settleKey)
      : null;
    travelStartTimeRef.current = performance.now();
  };

  const notifySettled = (target: DampedFollowTarget) => {
    if (
      target.settleKey === undefined ||
      target.settleKey === settledKeyRef.current
    ) {
      return;
    }
    settledKeyRef.current = target.settleKey;
    target.onSettled?.(target.settleKey);
  };

  const handle = useMemo<DampedFollowHandle>(() => {
    return {
      elementRef: (el) => {
        elRef.current = el;
        if (!el) {
          stopLoopRef.current();
          return;
        }
        // Seed synchronously on attach so the element never paints at
        // (0,0) waiting for the first rAF tick.
        const target = pendingTargetRef.current;
        committedTargetRef.current = target;
        if (!posRef.current && target) {
          posRef.current = target.snap
            ? { x: target.x, y: target.y }
            : { ...(target.initialPosition ?? target) };
          if (
            !target.snap &&
            target.initialPosition &&
            (target.initialPosition.x !== target.x ||
              target.initialPosition.y !== target.y)
          ) {
            startTravel(target.initialPosition, target);
          }
        }
        if (posRef.current) writePosition(el, posRef.current);
        if (
          target &&
          posRef.current?.x === target.x &&
          posRef.current?.y === target.y
        ) {
          notifySettled(target);
        }
        if (
          target &&
          posRef.current &&
          (posRef.current.x !== target.x || posRef.current.y !== target.y)
        ) {
          startLoopRef.current();
        }
      },
      setTarget: (target) => {
        pendingTargetRef.current = target;
      },
      getPosition: () => posRef.current,
    };
  }, []);

  useLayoutEffect(() => {
    let rafId = 0;
    let disposed = false;

    function stopLoop() {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
    }

    function startLoop() {
      const target = committedTargetRef.current;
      const pos = posRef.current;
      if (
        disposed ||
        rafId ||
        !elRef.current ||
        !target ||
        (pos && pos.x === target.x && pos.y === target.y)
      ) {
        return;
      }
      lastTimeRef.current = performance.now();
      rafId = requestAnimationFrame(tick);
    }

    function tick(now: number) {
      rafId = 0;
      const deltaMs = now - lastTimeRef.current;
      lastTimeRef.current = now;
      const target = committedTargetRef.current;
      const el = elRef.current;
      if (!target || !el) return;

      let next =
        !posRef.current || target.snap
          ? { x: target.x, y: target.y }
          : target.durationMs !== undefined && travelRef.current
            ? (() => {
                const duration = Math.max(0, target.durationMs);
                const progress =
                  duration === 0
                    ? 1
                    : Math.min(
                        (now - travelStartTimeRef.current) / duration,
                        1,
                      );
                return penTravelPoint(
                  travelRef.current,
                  easeInOutCubic(progress),
                );
              })()
            : (target.constrain ?? keepPosition)(
                dampPoint(
                  posRef.current,
                  target,
                  smoothingRef.current,
                  deltaMs,
                ),
                target,
              );
      if (
        Math.hypot(target.x - next.x, target.y - next.y) <= SETTLED_DISTANCE_PX
      ) {
        next = { x: target.x, y: target.y };
      }
      posRef.current = next;
      writePosition(el, next);
      if (next.x !== target.x || next.y !== target.y) {
        rafId = requestAnimationFrame(tick);
      } else {
        notifySettled(target);
      }
    }

    startLoopRef.current = startLoop;
    stopLoopRef.current = stopLoop;
    startLoop();
    return () => {
      disposed = true;
      startLoopRef.current = noop;
      stopLoopRef.current = noop;
      stopLoop();
    };
  }, []);

  useLayoutEffect(() => {
    smoothingRef.current = smoothingSeconds;
    const target = pendingTargetRef.current;
    const previous = committedTargetRef.current;
    committedTargetRef.current = target;
    if (!target) {
      posRef.current = null;
      stopLoopRef.current();
      return;
    }
    const changed =
      !previous ||
      previous.x !== target.x ||
      previous.y !== target.y ||
      !!previous.snap !== !!target.snap ||
      previous.settleKey !== target.settleKey ||
      previous.durationMs !== target.durationMs;
    if (previous?.settleKey !== target.settleKey) settledKeyRef.current = null;
    if (!changed) {
      if (elRef.current && posRef.current) {
        writePosition(elRef.current, posRef.current);
      }
      return;
    }
    if (posRef.current?.x === target.x && posRef.current?.y === target.y) {
      notifySettled(target);
      return;
    }
    startTravel(posRef.current, target);
    startLoopRef.current();
  });

  return handle;
}
